import { useState } from "react";
import {
  X,
  User,
  Calendar,
  Globe,
  Monitor,
  Smartphone,
  Eye,
  EyeOff,
  Copy,
  CheckCircle,
  Code2,
  ArrowRight,
  Info,
} from "lucide-react";
import { AuditTrailReport } from "../../services/auditTrail.service";

interface AuditTrailDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  report: AuditTrailReport | null;
}

export default function AuditTrailDetailModal({
  isOpen,
  onClose,
  report,
}: AuditTrailDetailModalProps) {
  const [activeTab, setActiveTab] = useState<"overview" | "data" | "device">(
    "overview"
  );
  const [showRawData, setShowRawData] = useState(false);
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setActiveTab("overview");
    setShowRawData(false);
    setCopied(false);
    onClose();
  };

  if (!isOpen || !report) return null;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-GB", {
      timeZone: "Asia/Kolkata",
      day: "numeric",
      month: "short",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const getEventTypeColor = (eventType: string) => {
    const colors = {
      LOGIN: "bg-green-100 text-green-800",
      LOGOUT: "bg-gray-100 text-gray-800",
      SETTINGS_UPDATED: "bg-blue-100 text-blue-800",
      USER_CREATED: "bg-purple-100 text-purple-800",
      USER_UPDATED: "bg-yellow-100 text-yellow-800",
      FILE_UPLOADED: "bg-indigo-100 text-indigo-800",
      FILE_DOWNLOADED: "bg-cyan-100 text-cyan-800",
      DOCUMENT_VIEWED: "bg-orange-100 text-orange-800",
      ERROR: "bg-red-100 text-red-800",
      WARNING: "bg-amber-100 text-amber-800",
    };
    return (
      colors[eventType as keyof typeof colors] || "bg-gray-100 text-gray-800"
    );
  };
  const getActionByName = () => {
    // 1. Standard events (like Document Search)
    if (report.user_info?.name) return report.user_info.name;

    // 2. Social Login events (Nested inside data -> user -> name)
    const socialName = report.data?.user?.name;
    if (socialName) {
      return `${socialName.givenName || ""} ${socialName.familyName || ""}`.trim();
    }

    // USER_CREATED
    const newDataName = report.data?.new_data?.name;
    if (newDataName && (newDataName.givenName || newDataName.familyName)) {
      return `${newDataName.givenName || ""} ${newDataName.familyName || ""}`.trim();
    }

    // 3. Fallback to Email
    if (report.user_info?.email) return report.user_info?.email;

    // 4. Fallback to ID or "N/A"
    return report.action_by || "N/A";
  };

  const renderJsonDiff = () => {
    if (!report.data || (!report.data.old_data && !report.data.new_data)) {
      return (
        <div className="text-center py-8">
          <Info className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500">
            No change data available for this event
          </p>
        </div>
      );
    }

    const hasChanges = report.data.old_data && report.data.new_data;

    return (
      <div className="space-y-4">
        {hasChanges ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <div className="w-3 h-3 bg-red-200 rounded-full"></div>
                Before (Old Data)
              </div>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 max-h-96 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {JSON.stringify(report.data.old_data, null, 2)}
                </pre>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <div className="w-3 h-3 bg-green-200 rounded-full"></div>
                After (New Data)
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-h-96 overflow-auto">
                <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                  {JSON.stringify(report.data.new_data, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <div className="w-3 h-3 bg-blue-200 rounded-full"></div>
              Event Data
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-h-96 overflow-auto">
              <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono">
                {JSON.stringify(report.data, null, 2)}
              </pre>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2 pt-4 border-t border-gray-200">
          <button
            onClick={() => setShowRawData(!showRawData)}
            className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
          >
            {showRawData ? (
              <EyeOff className="w-3 h-3" />
            ) : (
              <Eye className="w-3 h-3" />
            )}
            {showRawData ? "Hide" : "Show"} Raw JSON
          </button>
          <button
            onClick={() =>
              copyToClipboard(JSON.stringify(report.data, null, 2))
            }
            className="text-sm text-gray-600 hover:text-gray-700 flex items-center gap-1"
          >
            {copied ? (
              <CheckCircle className="w-3 h-3" />
            ) : (
              <Copy className="w-3 h-3" />
            )}
            {copied ? "Copied!" : "Copy"}
          </button>
        </div>

        {showRawData && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <pre className="text-sm text-gray-800 whitespace-pre-wrap font-mono max-h-64 overflow-auto">
              {JSON.stringify(report, null, 2)}
            </pre>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-full flex items-center justify-center">
              <Eye className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Audit Trail Details
              </h2>
              <div className="flex items-center gap-2 mt-1">
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getEventTypeColor(
                    report.event_type
                  )}`}
                >
                  {report.event_type.replace(/_/g, " ")}
                </span>
                <span className="text-sm text-gray-500">
                  {formatDate(report.event_time)}
                </span>
              </div>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 p-1 rounded-lg"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 px-6">
          {[
            { id: "overview", label: "Overview", icon: Info },
            { id: "data", label: "Data Changes", icon: Code2 },
            { id: "device", label: "Device Info", icon: Monitor },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${activeTab === tab.id
                  ? "border-blue-500 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
                }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === "overview" && (
            <div className="space-y-6">
              {/* Event Overview */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Event Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Event Time
                        </div>
                        <div className="text-sm text-gray-600">
                          {formatDate(report.event_time)}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Globe className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          IP Address
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.ip_address}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Info className="w-4 h-4 text-gray-400 mt-0.5" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Description
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.event_description}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    User Information
                  </h3>
                  <div className="space-y-3">
                    {report.user_info ? (
                      <>
                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              User Email
                            </div>
                            <div className="text-sm text-gray-600">
                              {report.user_info.email}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <User className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              User ID
                            </div>
                            <div className="text-sm text-gray-600 font-mono">
                              {report.user_id}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-3">
                          <ArrowRight className="w-4 h-4 text-gray-400" />
                          <div>
                            <div className="text-sm font-medium text-gray-700">
                              Action By
                            </div>
                            <div className="text-sm text-gray-600 font-mono">
                              {getActionByName()}
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="text-sm text-gray-500 italic">
                        No user information available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "data" && renderJsonDiff()}

          {activeTab === "device" && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    Browser Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Monitor className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Browser
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.device_info?.browser?.name || "Unknown"}{" "}
                          {report.device_info?.browser?.version || ""}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Code2 className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Engine
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.device_info?.engine?.name || "Unknown"}{" "}
                          {report.device_info?.engine?.version || ""}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    System Information
                  </h3>
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <Smartphone className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          Operating System
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.device_info?.os?.name || "Unknown"}{" "}
                          {report.device_info?.os?.version || ""}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      <Monitor className="w-4 h-4 text-gray-400" />
                      <div>
                        <div className="text-sm font-medium text-gray-700">
                          CPU Architecture
                        </div>
                        <div className="text-sm text-gray-600">
                          {report.device_info?.cpu?.architecture || "Unknown"}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Full User Agent */}
              <div className="space-y-2">
                <h3 className="text-lg font-medium text-gray-900">
                  User Agent
                </h3>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <div className="text-sm text-gray-800 font-mono break-all">
                    {report.device_info?.ua ||
                      "User agent information not available"}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
